import { useCallback, useEffect, useRef, useState } from "react";

export interface UseViewportObserverOptions {
  threshold?: number;
  duration?: number;
  rootMargin?: string;
}

export interface UseViewportObserverReturn<T extends HTMLElement> {
  ref: React.RefObject<T | null>;
  isVisible: boolean;
  hasTriggered: boolean;
}

export const useViewportObserver = <T extends HTMLElement>(
  onVisible: () => void,
  options: UseViewportObserverOptions = {}
): UseViewportObserverReturn<T> => {
  const { threshold = 0.5, duration = 1000, rootMargin = "0px" } = options;

  const elementRef = useRef<T | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTriggeredRef = useRef(false);
  const onVisibleRef = useRef(onVisible);

  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  // Keep the callback ref up to date
  useEffect(() => {
    onVisibleRef.current = onVisible;
  }, [onVisible]);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (!entry) return;

      const isIntersecting =
        entry.isIntersecting && entry.intersectionRatio >= threshold;
      setIsVisible(isIntersecting);

      if (isIntersecting) {
        // Don't start timer if already triggered
        if (hasTriggeredRef.current) return;

        // Start timer when element becomes visible with sufficient threshold
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
          if (!hasTriggeredRef.current) {
            hasTriggeredRef.current = true;
            setHasTriggered(true);
            onVisibleRef.current();
          }
        }, duration);
      } else if (timerRef.current) {
        // Clear timer if element is no longer visible or below threshold
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    [threshold, duration]
  );

  useEffect(() => {
    const element = elementRef.current;

    if (element) {
      // Create intersection observer
      observerRef.current = new IntersectionObserver(handleIntersection, {
        threshold,
        rootMargin,
      });

      observerRef.current.observe(element);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [handleIntersection, threshold, rootMargin]);

  return {
    ref: elementRef,
    isVisible,
    hasTriggered,
  };
};
